import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { connect } from 'react-redux';
import { IStore } from 'redux/IStore';
import { withRouter, RouteComponentProps, Link } from 'react-router';
import { setAppointmentFiltersDate } from 'redux/modules/shop/appointments';
import { PortalDatePicker, PortalSelectMenu } from 'components/FormControls';
import { addURLSearchParams, idFromEntityId, readURLSearchParams } from 'helpers/common';
import moment = require('moment');
import { Button, ButtonRound, WhiteButton } from 'components/Common/Button';
import renderIcon from 'helpers/renderIcon';
import { Modal } from 'components';
import { IDropdownData } from 'models/common';
import { getStaff } from '../helpers';
import { Dispatch } from 'redux';
import { setBookingTimetablesFiltersDate } from 'redux/modules/shop/bookingtimetables';
import { IDatePickerProps } from 'components/FormControls/PortalDatePicker';
import { IHoliday } from 'models/shop/appointments';
import { isAdmin, momentWithTimezone } from 'redux/modules/shop/helpers';

interface IOwnProps {
	section: 'appointments' | 'bookingTimetables';
	officeId: string;
	changeOfficeId: (id: string, push: boolean) => void;
	changeMemberId?: (id: string) => void;
	publicHolidays?: IHoliday[];
	isTodayHoliday?: (holidays: IHoliday[], date: string) => boolean;
	timezone?: string;
}

interface IRouterParams {
	id: string;
	code: string;
	officeId?: string | undefined;
}

interface ITranslationProps {
	t?: (key: string) => string;
}

type IRouterProps = RouteComponentProps<IRouterParams, {}>;

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ReturnType<typeof dispatchToProps> &
	IRouterProps &
	ITranslationProps;

interface IState {
	modalOpen: boolean;
	datepicker: boolean;
	// When the office is changed, we have to find the previous user in the new office
	findUser?: {
		officeId: string;
		userId: string;
	};
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopAppointmentsFilterHeaderComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);
		this.state = {
			modalOpen: false,
			datepicker: false,
		};

		this.toggleModal = this.toggleModal.bind(this);
		this.toggleDatepicker = this.toggleDatepicker.bind(this);
		this.setOffice = this.setOffice.bind(this);
		this.setStaff = this.setStaff.bind(this);
		this.setDate = this.setDate.bind(this);
		this.clickListener = this.clickListener.bind(this);
	}

	calendarRef = React.createRef<HTMLDivElement>();

	public componentDidMount(): void {
		document.addEventListener('click', this.clickListener);

		const dateFromUrl = readURLSearchParams('calendarDate');
		if (dateFromUrl) {
			this.setDate(momentWithTimezone(this.props.timezone, dateFromUrl));
		}
	}

	public componentWillUnmount(): void {
		document.removeEventListener('click', this.clickListener);
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		// close datepicker when date changes
		if (prevProps.filterDate !== this.props.filterDate && this.state.datepicker) {
			this.setState({ datepicker: false });
		}

		// If the new office is loaded, find the previous user in the new staff members
		// If the user was not found, select null (Everyone)
		if (this.state.findUser && this.props.officeId == this.state.findUser.officeId) {
			const foundStaffMemberFromUserId = this.props.staff
				.map((person) => ({
					key: 'id' in person ? person.id : idFromEntityId(person.entityId),
					userId: person.userId,
				}))
				.find((person) => person.userId == this.state.findUser.userId)?.key;

			this.setStaff(foundStaffMemberFromUserId);
			this.setState({ findUser: null });
		}
	}

	private clickListener(e: MouseEvent) {
		const target = e.target;
		if (!this.state.datepicker || !this.calendarRef.current || !target) {
			return;
		}
		if (!this.calendarRef.current.contains(target as any)) {
			this.setState({ datepicker: false });
		}
	}

	private toggleModal() {
		this.setState((s) => ({
			modalOpen: !s.modalOpen,
		}));
	}

	private toggleDatepicker() {
		this.setState((s) => ({ datepicker: !s.datepicker }));
	}

	private setOffice(id: string) {
		const selectedUser = this.props.staff
			.map((person) => ({
				key: 'id' in person ? person.id : idFromEntityId(person.entityId),
				userId: person.userId,
			}))
			.find((person) => person.key == this.props.filterStaffId)?.userId;

		this.props.changeOfficeId(id, true);

		if (selectedUser) {
			// Set this and wait for the new office to load
			this.setState({
				findUser: {
					officeId: id,
					userId: selectedUser,
				},
			});
		}
	}

	private setStaff(id: string) {
		if (id === 'ALL') {
			id = '';
		}
		this.props.changeMemberId(id);
	}

	private setDate(date: moment.Moment) {
		addURLSearchParams('calendarDate', date.toISOString());
		this.props.setFiltersDate(date.toISOString());
	}

	private renderOfficeSelect() {
		let offices = this.props.offices || [];
		const isSiteAdmin: boolean = this.props.userPreferences.isAdministrator;
		const isShopAdmin = isAdmin(this.props.shopId, this.props.userPreferences.userRoles);
		const personalOfficesFilter =
			isShopAdmin || isSiteAdmin
				? null
				: this.props.userPreferences.userRoles?.filter((role) => role.type == 'Office').map((role) => role.objId) || [];
		if (personalOfficesFilter) {
			offices = offices.filter((o) => personalOfficesFilter.includes(o.id));
		}
		const { t, params } = this.props;
		const selectedOffice = params.officeId;
		if (offices.length === 0) {
			return null;
		}
		const options = offices.map((off) => ({
			key: off.id,
			value: off.name || off.location,
		}));
		return (
			<PortalSelectMenu
				label={t('Shop_ManageShop_General_Label_Location')}
				input={{
					onChange: this.setOffice,
					value: selectedOffice,
				}}
				isRequired={true}
				items={options}
				searchable={false}
			/>
		);
	}

	private renderStaffSelect() {
		const { t, filterStaffId, section, staff } = this.props;
		if (!staff) {
			return null;
		}
		if (!staff.length) {
			return (
				<div className="fieldGroup">
					<div className="inputField">{t('Shop_Appointments_Filter_Staff_Empty')}</div>
				</div>
			);
		}
		const options: IDropdownData[] = staff.map((person) => ({
			key: 'id' in person ? person.id : idFromEntityId(person.entityId),
			value: person.name,
			image: person.picture,
		}));
		if (section === 'appointments') {
			options.unshift({
				key: 'ALL',
				value: this.props.t('Shop_Appointments_Filter_Staff_All'),
			});
		}

		return (
			<PortalSelectMenu
				label={t('Shop_ManageShop_Members_Label_Staff')}
				input={{
					onChange: (s) => {
						this.setStaff(s);
						this.toggleModal();
					},
					value: filterStaffId || 'ALL',
				}}
				items={options}
				isRequired={true}
				withImages={true}
				searchable={false}
			/>
		);
	}

	private renderDateSelect(date: moment.Moment, isWeek: boolean) {
		const previous = date.clone().add(isWeek ? -7 : -1, 'days');
		const next = date.clone().add(isWeek ? 7 : 1, 'days');
		let calendar: JSX.Element;
		if (this.state.datepicker) {
			const calendarProps: IDatePickerProps = {
				input: {
					value: date.toDate(),
					onChange: (d: Date) => {
						// Check if same day or same week as the onChange triggers when opening the datepicker
						if (
							(this.props.filterMode === 'day' &&
								!momentWithTimezone(this.props.timezone, date)
									.startOf('day')
									.isSame(momentWithTimezone(this.props.timezone, d).startOf('day'))) ||
							(this.props.filterMode === 'week' &&
								!momentWithTimezone(this.props.timezone, date)
									.startOf('isoWeek')
									.isSame(momentWithTimezone(this.props.timezone, d).startOf('isoWeek')))
						) {
							this.setDate(momentWithTimezone(this.props.timezone, d));
						}
					},
				},
				inline: true,
				timezone: this.props.timezone,
				putSelectedValueInUrlQuery: 'calendarDate',
			};
			calendar = (
				<div className="calendar">
					<PortalDatePicker {...calendarProps} />
				</div>
			);
		}
		let title = (
			<span>
				<strong>{date.format('ddd')}</strong> {date.format('L')}
			</span>
		);
		if (isWeek) {
			title = (
				<span>
					<strong>{date.format('MMMM')}</strong> {date.format('YYYY')}
				</span>
			);
		}
		return (
			<div className="calendarDateSelect" ref={this.calendarRef}>
				<button onClick={() => this.setDate(previous)}>{renderIcon('back')}</button>
				<span onClick={this.toggleDatepicker}>{title}</span>
				<button onClick={() => this.setDate(next)}>{renderIcon('back')}</button>
				{calendar}
			</div>
		);
	}

	private renderModal() {
		const { section, t } = this.props;

		if (!this.state.modalOpen) {
			return null;
		}

		const footer: JSX.Element = (
			<div className="filterAppointments__footer">
				<WhiteButton onClick={() => this.toggleModal()}>{t('BSPortal_Common_Close')}</WhiteButton>
			</div>
		);

		return (
			<Modal
				closeModal={this.toggleModal}
				// showHeaderCloseButton={true}
				title={this.props.t('Shop_Appointments_Filter_Title')}
				modalClass={'filterAppointments'}
				footer={footer}
			>
				{this.renderOfficeSelect()}
				{section === 'appointments' ? this.renderStaffSelect() : null}
			</Modal>
		);
	}

	private renderWorkTimeButton() {
		const { t, section, params } = this.props;
		const filterOfficeId = params.officeId;
		if (section === 'bookingTimetables') {
			return (
				<Link to={'/shop/' + params.id + '/appointments/' + filterOfficeId}>
					<Button>{t('Shop_WorkTime_EditHours_ButtonAppointments')}</Button>
				</Link>
			);
		}
		if (section === 'appointments' && filterOfficeId) {
			return (
				<Link to={'/shop/' + params.id + '/worktime/' + filterOfficeId}>
					<Button>{t('Shop_WorkTime_EditHours_Button')}</Button>
				</Link>
			);
		}
	}

	public render() {
		const { offices, filterDate, filterMode, section, params, timezone } = this.props;
		const filterOfficeId = params?.officeId;

		if (!offices) {
			return null;
		}

		var isHoliday =
			this.props.isTodayHoliday != null && this.props.publicHolidays != null && !this.props?.filterStaffId
				? this.props.isTodayHoliday(this.props.publicHolidays, filterDate)
				: false;

		return (
			<div
				className="shopAppointmentsFilterHeaderContainer"
				style={{ zIndex: `${this.state.modalOpen ? '1001' : '801'}` }}
			>
				<div className="shopAppointmentsFilterHeader">
					{this.renderOfficeSelect()}
					{this.props.changeMemberId ? this.renderStaffSelect() : null}
					{/* Temporary public holiday tag until we have a redesign for the calendar */}
					{isHoliday ? (
						<div className="holdayTagContainer">
							<div className="space" />
							<div className="holiday">
								<p>{this.props.t('Public Holiday')}</p>
							</div>
							<div className="space" />
						</div>
					) : null}
					{this.renderDateSelect(momentWithTimezone(timezone, filterDate), filterMode === 'week')}
					{this.renderWorkTimeButton()}
					<div className="mobileFilter">
						{section === 'appointments' && filterOfficeId ? (
							<Link
								to={'/shop/' + params.id + '/worktime/' + filterOfficeId}
								className="workTimeAppointmentsButtonSwitch"
							>
								<ButtonRound>{renderIcon('nikaWorktime', 28, 28)}</ButtonRound>
							</Link>
						) : (
							<Link
								to={'/shop/' + params.id + '/appointments/' + filterOfficeId}
								className="workTimeAppointmentsButtonSwitch"
							>
								<ButtonRound>{renderIcon('nikaCalendar', 28, 28)}</ButtonRound>
							</Link>
						)}
						<ButtonRound onClick={this.toggleModal}>{renderIcon('filter')}</ButtonRound>
						{this.renderModal()}
					</div>
				</div>
			</div>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => ({
	offices: state.manageShop?.shop?.offices,
	shopId: state.manageShop?.shop?.id,
	staff: getStaff(state, ownProps.officeId),
	filterStaffId: state[ownProps.section].memberId,
	filterDate: ownProps.section === 'appointments' ? state[ownProps.section].rawDate : state[ownProps.section].date,
	filterMode: ownProps.section === 'appointments' ? state.appointments.mode : 'week',
	userPreferences: state.userPreferences,
});

const dispatchToProps = (dispatch: Dispatch<IStore>, ownProps: IOwnProps) => ({
	setFiltersDate: (date: string) =>
		dispatch(
			ownProps.section === 'appointments' ? setAppointmentFiltersDate(date) : setBookingTimetablesFiltersDate(date),
		),
});

const ShopAppointmentsFilterHeader = withRouter(
	connect(stateToProps, dispatchToProps)(ShopAppointmentsFilterHeaderComponent) as any,
) as React.ComponentType<IOwnProps>;

export { ShopAppointmentsFilterHeader };
